import { ChevronRightIcon } from "@chakra-ui/icons";
import { Skeleton as ChakraSkeleton, Divider } from "@chakra-ui/react";
import { cn } from "@peerigon/pupper/tailwind";
import Link from "next/link";
import { Fragment } from "react";
import { useTranslatedString } from "src/i18n/hooks";
import { NotFoundIcon } from "./NotFoundIcon";
import { useSearchContext } from "./hooks";
import { CommonSearchTerms } from "./shared";

export const HeaderSearchResult = () => {
	const t = useTranslatedString();
	const {
		state,
		data,
		isEmptyInput,
		isLoading,
		isEmptyResult,
		isError,
		closeSearch,
		commonSearchTerms,
	} = useSearchContext();

	// Is there more than one page of results (threshold is 5 results)? If yes, we show the "show more" link
	const showMore = data && data.pageResults.length < data.totalResults;

	const common = {
		className:
			"overflow-hidden w-full max-w-container mt-0 flex flex-col bg-brand-light-bright lg:bg-white rounded-none lg:rounded-md lg:-mt-3 lg:border border-brand-light mx-auto h-[calc(100%-88px)] lg:h-auto lg:p-2 p-4 pb-6 lg:pb-2 pt-0",
	};

	if (isEmptyInput) {
		if (commonSearchTerms.length === 0)
			return <div className={cn(common.className, "block lg:hidden")} />;

		return (
			<div {...common}>
				<CommonSearchTerms className="px-3 py-2" />
			</div>
		);
	}

	if (isLoading)
		return (
			<div {...common}>
				{Array.from({ length: 3 }).map((_, index) => (
					<div
						className="flex items-center gap-1 py-3 px-2"
						// eslint-disable-next-line react/no-array-index-key
						key={index}
					>
						<div className="text-brand-blue opacity-50">
							<ChevronRightIcon className="size-6!" />
						</div>
						<div className="flex flex-col gap-2 w-full">
							<ChakraSkeleton className="h-4 lg:h-5 w-full" />
							<ChakraSkeleton className="h-3 lg:h-4 w-3/4" />
						</div>
					</div>
				))}
			</div>
		);

	if (isError || isEmptyResult)
		return (
			<div {...common}>
				<div className="flex flex-col items-center justify-center lg:flex-row lg:gap-2 w-full h-full lg:py-2 lg:px-3">
					<div className="p-6 mb-2 rounded-full border border-brand-light w-fit bg-white lg:p-2 lg:mb-0 ">
						<NotFoundIcon className="lg:size-8" />
					</div>

					<span className="py-3 px-2 w-full text-center lg:text-left">
						{t("search.notFound")}
					</span>
				</div>
			</div>
		);

	return (
		<div {...common}>
			<div className="flex flex-col gap-2 h-full overflow-y-auto">
				{data?.pageResults.map((result) => {
					const url = new URL(result.url);
					const pathname = url.pathname;

					return (
						<Fragment key={result.id}>
							<Link
								id="click_search_result"
								href={pathname}
								className="w-full flex gap-1 items-center px-2 py-3 no-underline! hover:bg-brand-light-bright"
							>
								<div className="text-brand-blue">
									<ChevronRightIcon className="size-6!" />
								</div>

								<div>
									<p className="text-md  font-normal">
										{result.title}
									</p>
									<p className="text-sm">
										{t("search.text", {
											text: result.bodyExcerpt,
										})}
									</p>
								</div>
							</Link>
						</Fragment>
					);
				})}
			</div>
			{showMore && (
				<div className="flex flex-col justify-end">
					<div className="py-3">
						<Divider />
					</div>
					<div className="text-center lg:text-left">
						<Link
							href={`/${t("search.slug")}?searchTerm=${state.query}`}
							onClick={closeSearch}
							className="py-3 px-2 inline-block w-full"
						>
							{t("search.showMore", {
								count: data.totalResults,
							})}
						</Link>
					</div>
				</div>
			)}
		</div>
	);
};

// 🔬 TBD
